import React from 'react';

import { Box, Button, Link, Text } from '@opendoor/bricks-next';
import type { GestureResponderEvent } from 'react-native';

export interface ISetupAccountFormProps {
  /**
   * Name of the form in analytics events, should be unique within the app
   * Convention: {appName}-{accountFormName} or {appName}-{resetPasswordFormName}
   */
  analyticsName: string;
  email: string;
  hasGeneratedPassword?: boolean;
  onResetValidEmail: (e?: React.SyntheticEvent | GestureResponderEvent) => void;
  requestResetPassword?: (email: string) => void;
}

export const CheckEmailForm: React.FC<ISetupAccountFormProps> = ({
  hasGeneratedPassword,
  email,
  analyticsName,
  onResetValidEmail,
  requestResetPassword,
}) => {
  const headerText = hasGeneratedPassword ? 'Set up your account' : 'Check your email',
    emailInstructionText = hasGeneratedPassword
      ? `First, check your email and click the link to set up your password. We emailed a link to ${email}.`
      : `We’ve sent reset password instructions to ${email}. Once you’ve reset your password, sign in again.`;

  return (
    <Box gap="$20x">
      <Box gap="$10x">
        <Text tag="h2" typography="$headerMedium">
          {headerText}
        </Text>
        <Text tag="p" typography="$bodySmall">
          {emailInstructionText}
        </Text>
      </Box>

      {hasGeneratedPassword ? (
        <>
          {requestResetPassword && (
            <Button
              variant="primary"
              size="small"
              label="Resend email"
              onPress={() => requestResetPassword(email)}
              analyticsName={`${analyticsName}-resend-reset-password-email`}
              aria-label="Resend email"
            />
          )}
          <Text tag="p" typography="$bodyMedium">
            We’ve sent reset password instructions to {email}. Once you’ve reset your password,{' '}
            <Link
              variant="primary"
              href="/login"
              size="medium"
              onPress={onResetValidEmail}
              analyticsName={`${analyticsName}-sign-in-after-reset-password`}
              aria-label="Sign In"
              data-testid="sign-in-after-reset-password"
            >
              sign in again
            </Link>
          </Text>
        </>
      ) : (
        <Button
          variant="primary"
          size="small"
          label="Sign In"
          onPress={onResetValidEmail}
          analyticsName={`${analyticsName}-sign-in-after-reset-password`}
          aria-label="Sign In"
          data-testid="sign-in-after-reset-password"
        />
      )}
    </Box>
  );
};
